import { Injectable, type Provider } from '@angular/core';

import { err, makeError, ok, type ApiResult } from '../../../core/models/api-result';
import type {
  HubInsights,
  HubInsightsGranularity,
  HubInsightsJoinSourceCount,
  HubInsightsMemberPoint,
  HubInsightsPost,
  HubInsightsRange,
  HubInsightsTimeOfWeekBucket,
  HubInsightsVisitBucket,
} from './hub-insights.models';
import { rangeDays } from './hub-insights.models';
import { HubInsightsService } from './hub-insights.service';

/**
 * HUB INSIGHTS WITHOUT A SESSION (`/preview/hubs`, board 14).
 *
 * `hubInsights` is manager-only and viewer-scoped, so the harness answers from
 * memory, in the API's own shape and by its own rules (suppressed counts are
 * null, complementary buckets go in pairs, routes under 5 fold into the
 * `source: null` row). The two hubs the harness viewer may manage:
 *
 *   · Coffee Club (`?hub=coffee`): a young club with the games leaderboard on.
 *     30 and 90 days are healthy; 7 days is QUIET, so every people count is
 *     suppressed and the page has to say "Fewer than 5" throughout. Visit
 *     history starts 40 days ago, so 90 days says "Counting since …".
 *   · Rum Runner Works (`?hub=bhm`): an office. No membership history (the
 *     directory decides who is in), no games.
 *   · `&insights=forbidden` answers FORBIDDEN, as the server does for someone
 *     who lost the role mid-session: the page goes back to the feed.
 *
 * Small and inline: this file is imported as a value by `app.routes.ts`.
 */

const DAY = 86_400_000;

function todayUtc(): number {
  const now = new Date();
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

const iso = (t: number) => new Date(t).toISOString();

/** Bucket starts: one per day, or Mondays with the first at the window start. */
function bucketStarts(from: number, to: number, granularity: HubInsightsGranularity): number[] {
  const starts = [from];
  if (granularity === 'DAY') {
    for (let t = from + DAY; t <= to; t += DAY) starts.push(t);
    return starts;
  }
  const weekday = (new Date(from).getUTCDay() + 6) % 7;
  for (let t = from + (7 - weekday) * DAY; t <= to; t += 7 * DAY) starts.push(t);
  return starts;
}

interface Shape {
  readonly total: number;
  readonly startMembers: number;
  readonly joinedLast7Days: number;
  readonly joinSources: readonly HubInsightsJoinSourceCount[];
  readonly joinSourcesAllTime: readonly HubInsightsJoinSourceCount[];
  readonly visitors: number | null;
  readonly visits: number | null;
  /** Days ago the visit history begins. */
  readonly recordedDaysAgo: number;
  /** Visits per bucket, oldest first, cycled; null = suppressed. */
  readonly visitPattern: readonly (readonly [number | null, number | null])[];
  readonly weekday: readonly (number | null)[];
  readonly hourPeak: number | null;
  readonly busiest: { weekday: number; hour: number; visitors: number } | null;
  readonly postCount: number;
  readonly medianSeen: number | null;
  readonly top: readonly HubInsightsPost[];
  readonly games: { plays: number | null; players: number | null } | null;
  readonly history: boolean;
}

function memberSeries(shape: Shape, from: number, to: number, granularity: HubInsightsGranularity): HubInsightsMemberPoint[] {
  if (!shape.history) return [];
  const starts = bucketStarts(from, to, granularity);
  const n = starts.length;
  let prev = shape.startMembers;
  return starts.map((t, i) => {
    // An S-curve from the first count to the total, with two steps where an
    // invite round and a cross-post landed.
    const p = n === 1 ? 1 : i / (n - 1);
    const eased = p * p * (3 - 2 * p);
    const members = i === n - 1 ? shape.total : Math.round(shape.startMembers + (shape.total - shape.startMembers) * eased);
    const joined = Math.max(0, members - prev);
    prev = members;
    return { start: iso(t), members, joined, left: 0 };
  });
}

function visitSeries(shape: Shape, from: number, to: number, granularity: HubInsightsGranularity): HubInsightsVisitBucket[] {
  const recorded = todayUtc() - shape.recordedDaysAgo * DAY;
  const starts = bucketStarts(from, to, granularity);
  const offset = Math.max(0, shape.visitPattern.length - starts.length);
  return starts.map((t, i) => {
    const next = starts[i + 1] ?? to + DAY;
    if (next <= recorded) return { start: iso(t), visitors: 0, visits: 0 };
    const [visits, visitors] = shape.visitPattern[(i + offset) % shape.visitPattern.length];
    return { start: iso(t), visitors, visits };
  });
}

function hourBuckets(shape: Shape): HubInsightsTimeOfWeekBucket[] {
  return Array.from({ length: 24 }, (_, index) => {
    if (shape.hourPeak === null) {
      // The quiet week: a few people around lunch, each hour under 5.
      const v = index === 12 || index === 13 ? null : 0;
      return { index, visitors: v, visits: v };
    }
    // Office hours, peaking at lunch; two small evening hours suppressed as a pair.
    if (index === 7 || index === 19) return { index, visitors: null, visits: null };
    if (index < 8 || index > 18) return { index, visitors: 0, visits: 0 };
    const visitors = Math.round(shape.hourPeak * Math.exp(-Math.pow(index - 12.5, 2) / 10));
    return { index, visitors: Math.max(5, visitors), visits: Math.max(5, Math.round(visitors * 1.6)) };
  });
}

function build(hubId: string, range: HubInsightsRange, shape: Shape): HubInsights {
  const days = rangeDays(range);
  const to = todayUtc();
  const from = to - (days - 1) * DAY;
  const memberGranularity: HubInsightsGranularity = range === 'LAST_90D' ? 'WEEK' : 'DAY';
  const visitGranularity: HubInsightsGranularity = range === 'LAST_7D' ? 'DAY' : 'WEEK';
  const series = memberSeries(shape, from, to, memberGranularity);
  return {
    hubId,
    range,
    from: iso(from),
    to: iso(to + DAY - 1),
    suppressionThreshold: 5,
    members: {
      total: shape.total,
      joined: series.reduce((s, p) => s + p.joined, 0),
      joinedLast7Days: shape.joinedLast7Days,
      left: 0,
      historyAvailable: shape.history,
      granularity: memberGranularity,
      series,
      joinSources: shape.joinSources,
      joinSourcesAllTime: shape.joinSourcesAllTime,
    },
    visits: {
      visitors: shape.visitors,
      visits: shape.visits,
      recordedFrom: iso(to - shape.recordedDaysAgo * DAY),
      granularity: visitGranularity,
      series: visitSeries(shape, from, to, visitGranularity),
      byWeekday: shape.weekday.map((v, index) => ({ index, visitors: v, visits: v === null ? null : Math.round(v * 5.6) })),
      byHour: hourBuckets(shape),
      busiestSlot: shape.busiest,
    },
    posts: {
      postCount: shape.postCount,
      truncated: false,
      medianSeen: shape.medianSeen,
      top: shape.top,
    },
    games: shape.games,
  };
}

const post = (
  itemId: string,
  title: string,
  daysAgo: number,
  seen: number | null,
  opened: number | null,
  spotlightResponses: number | null,
): HubInsightsPost => ({
  itemId,
  title,
  contentType: spotlightResponses !== 0 ? 'article' : 'post',
  publishedAt: iso(todayUtc() - daysAgo * DAY),
  seen,
  opened,
  spotlightResponses,
});

// 33 joined since a founding pair (the hub card says 35 members).
const COFFEE_ROUTES: readonly HubInsightsJoinSourceCount[] = [
  { source: 'INVITE', count: 12 },
  { source: 'CROSS_POST', count: 8 },
  { source: 'EXPLORE', count: 6 },
  // Small routes (a shared link, the game) folded together, plus joins from
  // before routes were kept. Never named.
  { source: null, count: 7 },
];

const COFFEE_ROUTES_90D: readonly HubInsightsJoinSourceCount[] = [
  { source: 'INVITE', count: 12 },
  { source: 'CROSS_POST', count: 8 },
  { source: 'EXPLORE', count: 6 },
  { source: null, count: 9 },
];

const COFFEE_TOP: readonly HubInsightsPost[] = [
  post('coffee-0', 'The new grinder is in: come and taste the difference', 9, 212, 88, 41),
  post('coffee-1', 'Latte art Friday: round 10, one round left', 4, 156, 97, 0),
  post('coffee-2', 'Beans from Kenya this month', 12, 64, 19, 0),
  post('coffee-3', 'Who keeps taking the oat milk?', 2, 23, 9, null),
  post('coffee-4', 'Barista course: two places left', 1, null, null, 0),
];

const COFFEE: Record<HubInsightsRange, Shape> = {
  LAST_30D: {
    total: 35,
    startMembers: 2,
    joinedLast7Days: 9,
    joinSources: COFFEE_ROUTES,
    joinSourcesAllTime: COFFEE_ROUTES,
    visitors: 214,
    visits: 1206,
    recordedDaysAgo: 40,
    visitPattern: [
      [44, 7],
      [120, 18],
      [310, 61],
      [352, 74],
      [424, 96],
    ],
    weekday: [34, 40, 52, 92, 58, 10, 8],
    hourPeak: 31,
    busiest: { weekday: 3, hour: 12, visitors: 31 },
    postCount: 37,
    medianSeen: 64,
    top: COFFEE_TOP,
    games: { plays: 240, players: 23 },
    history: true,
  },
  LAST_90D: {
    total: 35,
    startMembers: 0,
    joinedLast7Days: 9,
    joinSources: COFFEE_ROUTES_90D,
    joinSourcesAllTime: COFFEE_ROUTES_90D,
    visitors: 236,
    visits: 1288,
    recordedDaysAgo: 40,
    visitPattern: [
      [12, 5],
      [44, 7],
      [120, 18],
      [310, 61],
      [352, 74],
      [424, 96],
    ],
    weekday: [38, 44, 57, 101, 63, 12, 9],
    hourPeak: 34,
    busiest: { weekday: 3, hour: 12, visitors: 34 },
    postCount: 41,
    medianSeen: 58,
    top: COFFEE_TOP,
    games: { plays: 410, players: 31 },
    history: true,
  },
  // THE QUIET WEEK. Three people joined (1 to 4, so no routes at all), and
  // every people count is under 5: suppressed, never 0.
  LAST_7D: {
    total: 35,
    startMembers: 32,
    joinedLast7Days: 3,
    joinSources: [],
    joinSourcesAllTime: COFFEE_ROUTES,
    visitors: null,
    visits: null,
    recordedDaysAgo: 40,
    visitPattern: [
      [0, 0],
      [null, null],
      [0, 0],
      [null, null],
      [0, 0],
      [0, 0],
      [null, null],
    ],
    weekday: [null, 0, null, 0, null, 0, 0],
    hourPeak: null,
    busiest: null,
    postCount: 2,
    medianSeen: null,
    top: [post('coffee-3', 'Who keeps taking the oat milk?', 2, null, null, 0), post('coffee-4', 'Barista course: two places left', 1, null, null, 0)],
    games: { plays: null, players: null },
    history: true,
  },
};

const OFFICE_TOP: readonly HubInsightsPost[] = [
  post('bhm-4', 'The roof terrace is open, and the heaters arrive Thursday', 3, 388, 201, 57),
  post('bhm-1', 'Where everything moved on Level 3', 8, 342, 240, 0),
  post('bhm-2', 'Which day should the team lunch move to?', 5, 297, 118, 0),
  post('bhm-3', 'Lost and found: one very nice umbrella', 1, 141, 33, 0),
];

function office(range: HubInsightsRange): Shape {
  const k = range === 'LAST_7D' ? 0.3 : range === 'LAST_90D' ? 2.2 : 1;
  const scale = (n: number) => Math.round(n * k);
  return {
    total: 412,
    startMembers: 412,
    joinedLast7Days: 4,
    joinSources: [],
    joinSourcesAllTime: [],
    visitors: scale(366),
    visits: scale(2841),
    recordedDaysAgo: 120,
    visitPattern: [
      [scale(520), scale(140)],
      [scale(610), scale(166)],
      [scale(702), scale(181)],
      [scale(655), scale(172)],
      [scale(688), scale(175)],
      [scale(301), scale(122)],
      [scale(512), scale(160)],
    ],
    weekday: [scale(210), scale(260), scale(244), scale(251), scale(150), 0, 0],
    hourPeak: scale(120),
    busiest: { weekday: 1, hour: 12, visitors: scale(120) },
    postCount: scale(26),
    medianSeen: scale(188),
    top: OFFICE_TOP,
    games: null,
    history: false,
  };
}

@Injectable()
export class HubInsightsPreviewService extends HubInsightsService {
  protected override async fetch(hubId: string, range: HubInsightsRange): Promise<ApiResult<HubInsights | null>> {
    if (typeof location !== 'undefined' && location.hash.includes('insights=forbidden')) {
      return err(makeError('FORBIDDEN', 'Only the people who run this hub can see its insights.'));
    }
    const shape = hubId === 'bhm' ? office(range) : COFFEE[range];
    return ok(build(hubId, range, shape));
  }
}

export const HUB_INSIGHTS_PREVIEW_PROVIDER: Provider = {
  provide: HubInsightsService,
  useClass: HubInsightsPreviewService,
};
